import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Pencil, Plus, Trash2 } from 'lucide-react';
import type { DimensionValueToken } from '@sleed/bizcommerce-api';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { serverMessage } from '~/lib/api/server-error';
import { useDimensionValuesByDimension, useDimensionValueMutations } from '~/hooks/use-dimension-values';
import { DimensionValueEditDialog } from './value-edit-dialog';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	dimensionId: number | null;
	dimensionName: string;
}

const Bool = ({ on }: { on: boolean }) =>
	on ? <Check size={15} className="text-success-fg" /> : <span className="text-foreground-disabled">—</span>;

function Swatch({ hex }: { hex?: string | null }) {
	if (!hex) return <span className="text-foreground-disabled">—</span>;
	return (
		<span className="inline-flex items-center gap-1.5">
			<span className="h-4 w-4 rounded-sm border border-border" style={{ backgroundColor: hex }} />
			<span className="font-mono text-[11px] text-foreground-muted">{hex}</span>
		</span>
	);
}

/** Side panel hosting the values of a single dimension (parity with the legacy "values" modal). */
export function DimensionValuesSheet({ open, onOpenChange, dimensionId, dimensionName }: Props) {
	const { t } = useTranslation();
	const { data, isLoading, isError } = useDimensionValuesByDimension(open ? dimensionId : null);
	const { remove } = useDimensionValueMutations(dimensionId);

	const [editOpen, setEditOpen] = useState(false);
	const [editing, setEditing] = useState<DimensionValueToken | null>(null);
	const [deleteRow, setDeleteRow] = useState<DimensionValueToken | null>(null);

	const rows = [...(data ?? [])].sort((a, b) => (a.si ?? 0) - (b.si ?? 0));

	const confirmDelete = () => {
		if (!deleteRow?.id) return;
		remove.mutate(deleteRow.id, {
			onSuccess: () => {
				toast.success(t('common.delete'));
				setDeleteRow(null);
			},
			onError: (err: unknown) => toast.error(serverMessage(err))
		});
	};

	return (
		<>
			<Sheet open={open} onOpenChange={onOpenChange}>
				<SheetContent side="right" className="flex w-full flex-col gap-0 p-0 sm:max-w-2xl">
					<SheetHeader className="border-b border-border px-5 py-4">
						<SheetTitle>{t('attributes.dimensionValues.title')}</SheetTitle>
						<SheetDescription>{dimensionName}</SheetDescription>
					</SheetHeader>

					<div className="flex items-center justify-between px-5 py-3">
						<span className="text-xs text-foreground-muted">{t('attributes.dimensionValues.count', { count: rows.length })}</span>
						<Button
							size="sm"
							disabled={!dimensionId}
							onClick={() => {
								setEditing(null);
								setEditOpen(true);
							}}>
							<Plus size={15} /> {t('attributes.dimensionValues.add')}
						</Button>
					</div>

					<div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
						{isLoading ? (
							<Skeleton className="h-64 w-full rounded-md" />
						) : isError ? (
							<div className="py-12 text-center text-sm text-foreground-muted">{t('common.noResults')}</div>
						) : rows.length === 0 ? (
							<div className="py-12 text-center text-sm text-foreground-subtle">{t('common.noResults')}</div>
						) : (
							<div className="overflow-hidden rounded-md border border-border">
								<table className="w-full border-collapse text-left text-sm">
									<thead>
										<tr className="border-b border-border bg-elevated text-[10px] font-semibold tracking-[0.04em] text-foreground-subtle">
											<th className="px-3 py-2">{t('attributes.dimensionValues.columns.value')}</th>
											<th className="px-3 py-2">{t('attributes.dimensionValues.columns.color')}</th>
											<th className="px-3 py-2">{t('attributes.dimensionValues.columns.code')}</th>
											<th className="px-3 py-2 text-center">{t('attributes.dimensionValues.columns.visible')}</th>
											<th className="px-3 py-2 text-right">{t('attributes.dimensionValues.columns.sort')}</th>
											<th className="px-3 py-2" />
										</tr>
									</thead>
									<tbody>
										{rows.map(v => (
											<tr key={v.id} className="border-b border-border-subtle last:border-b-0 hover:bg-elevated">
												<td className="px-3 py-2">
													<div className="flex items-center gap-2">
														{v.img1 && <img src={v.img1} alt="" className="h-7 w-7 rounded border border-border object-cover" />}
														<span className="font-medium text-foreground">{v.vtxt || '—'}</span>
													</div>
												</td>
												<td className="px-3 py-2">
													<Swatch hex={v.chex} />
												</td>
												<td className="px-3 py-2 font-mono text-xs text-foreground-muted">{v.cd || '—'}</td>
												<td className="px-3 py-2 text-center">
													<div className="flex justify-center">
														<Bool on={Boolean(v.iv)} />
													</div>
												</td>
												<td className="px-3 py-2 text-right tabular-nums text-foreground-muted">{v.si}</td>
												<td className="px-3 py-2">
													<div className="flex items-center justify-end gap-0.5">
														<Button
															variant="ghost"
															size="icon"
															aria-label={t('common.edit')}
															onClick={() => {
																setEditing(v);
																setEditOpen(true);
															}}>
															<Pencil size={15} />
														</Button>
														<Button variant="ghost" size="icon" aria-label={t('common.delete')} onClick={() => setDeleteRow(v)}>
															<Trash2 size={15} className="text-danger-fg" />
														</Button>
													</div>
												</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				</SheetContent>
			</Sheet>

			{dimensionId != null && <DimensionValueEditDialog open={editOpen} onOpenChange={setEditOpen} did={dimensionId} initial={editing} />}

			<Dialog open={Boolean(deleteRow)} onOpenChange={o => !o && setDeleteRow(null)}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{t('attributes.dimensionValues.deleteTitle')}</DialogTitle>
						<DialogDescription>{t('attributes.dimensionValues.deleteBody', { name: deleteRow?.vtxt || '' })}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant="ghost" onClick={() => setDeleteRow(null)}>
							{t('common.cancel')}
						</Button>
						<Button variant="destructive" onClick={confirmDelete} disabled={remove.isPending}>
							{t('common.delete')}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
}
